<template>
    <div v-if="!totalError" class="w-full">
        <!--AMOUNT SEND-->
        <span class="text-600">{{ lang('amount_send') }}: </span>
        <div v-if="currenciesGet && loaded" class="w-full flex flex-column mt-2 mb-3 gap-1">
            <InputAmount :amount="amountGet" :currency="currencyGet" :currencies="currenciesGet" :min="minGetLimit" :max="maxGetLimit"
                @update:amount="val => amountGet = val" @update:currency="val => currencyGet = val" @request="goToRequest()" :disableInput="amountReceiveSelected || firstTransferIsOn"
                :disableSelect="firstTransferIsOn"
                digits="2" :isClearButtons="false" :isRequest="true" />
        </div>
        <Skeleton v-else height="3rem" class="mt-2 mb-3 border-round-xl"/>
        
        <!--FIRST TRANSFER-->
        <div v-if="canFirstTransfer && currencyGet && firstTransferSendLimit && !firstTransferSendLimit.isZero() && !amountReceiveSelected" class="w-full mt-2 mb-3">
            <InlineMessage severity="success" class="border-round-xl line-height-2 text-xs xl:text-md">
                {{lang('first_transfer')}} {{ splitNum(firstTransferSendLimit) }} {{ getCurSign(currencyGet) }}
            </InlineMessage>
            <div class="flex flex-row align-items-center mt-2 mb-4">
                <ToggleSwitch v-model="firstTransferIsOn" class="flex mr-2"/>
                <span class="flex text-600">
                    {{lang('do_first_transfer')}}
                    <span class="ml-2 pi pi-info-circle text-500 cursor-pointer" @click="showFirstTransferDisplay()"></span>
                </span>
            </div>
        </div>

        <!--AMOUNT RECEIVE (SELECTED)-->
        <span class="text-600">{{ lang('amount_receive') }}:</span>
        <div v-if="currenciesGet && loaded"
            class="w-full flex flex-column mt-2 mb-3 gap-1">
            <InputAmount :amount="amountReceive" :currency="currencyReceive" :currencies="currenciesReceive" :min="minReceiveLimit" :max="maxReceiveLimit"
                @update:amount="val => amountReceive = val" @update:currency="val => currencyReceive = val" @request="goToRequest()"
                :amount_array="amountReceiveList" :amount_array_selected="amountReceiveSelected" @update:amount_array_selected="val => amountReceiveSelected = val"
                :disableInput="firstTransferIsOn"
                :disableSelect="firstTransferIsOn"
                digits="2" :isClearButtons="false" :isRequest="true" currencyTitleKey="currency_receive"/>
        </div>
        <Skeleton v-if="!loaded || !currenciesGet" height="3rem" class="mt-2 mb-3 border-round-xl"/>

        <!--GET PAY SYS-->
        <span class="text-600">{{ lang('payment_method') }}:</span>

        <PaymentSysDropdown v-if="loaded && getPaySys" 
            :pay_sys="getPaySys" 
            :currency="currencyGet" 
            type="get" 
            :list="sendPaySysListGrouped"
            :headerText="lang('choose_senders_bank')"
            @update:pay-sys="val => chooseGetPaySys(val)" />
        <Skeleton v-else height="3rem" class="mt-2 mb-3 border-round-xl"/>

        <!--SENDER NAME-->
        <div v-if="user_name" class="flex flex-row mb-3 align-items-center">
            <span class="flex text-500">{{ lang('sender_name') }}: &nbsp</span>
            <span v-if="isUserVerified" class="pi pi-verified text-primary cursor-pointer text-xs font-medium mr-1"></span>
            <span class="flex text-500 cursor-pointer" @click="goToProfile()"> 
                {{ user_name }} &nbsp
            </span>
            <span class="pi pi-pencil text-500 cursor-pointer" @click="goToProfile()"></span>
        </div>

        <!--INFO-->
        <div v-if="currenciesGet && currencyGet && loaded" class="mt-3 mb-3 relative" :class="(infoOnDisplay) ? 'surface-100 py-2 px-3 border-round-lg' : ''">
            <div class="flex flex-1 text-500">
                <span>{{ lang('guaranteed_rate') }}:</span>&nbsp{{ getBigRate(resultRate) }}
            </div>
            <p v-if="infoOnDisplay && comission_service && !firstTransferIsOn" class="mt-0 mb-1 text-sm text-700">
                <span class="text-500">{{ lang('service_comission') }}:</span> {{ comissionToDisplay }}
            </p>
            <div v-if="cashbackAvailable && !firstTransferIsOn" class="flex flex-row align-items-center mt-2 mb-1">
                <ToggleSwitch v-model="useCashback" class="flex mr-2"/>
                <span class="flex text-500">
                    {{ lang('use_cashback') }}
                    <span v-if="cashbackAvailable && useCashback && !firstTransferIsOn">
                        :&nbsp;{{ cashbackToDisplay }}
                    </span>
                </span>
            </div>
            <p v-if="infoOnDisplay && resultRate" class="mt-0 mb-1 text-sm text-700">
                <span class="text-500">{{ lang('guaranteed_rate_total') }}:</span> {{ getBigRate(resultRate) }}
            </p>
        </div>

        <!--RECIPIENTS-->
        <div v-if="loaded && recipientsAllowed && recipientChoosen && !hideRecipients">
            <span class="text-600">{{ lang('recipient_list') }}:</span>
            <RecipientsDropdown 
                :recipients="recipientsAllowed" 
                :recipient="recipientChoosen" 
                :pay_list="arrPaymetSystems"
                @update:recipient="val => recipientChoosen = val" 
                />
        </div>
        <Skeleton v-if="!loaded || !recipients" height="3rem" class="mt-2 mb-3 border-round-xl"/>

        <div v-if="loaded && recipientChoosen" class="w-full mb-3">
            <span class="cursor-pointer text-primary" @click="addRecipient(true)">
                <span class="mr-2">{{ lang('create_recipient') }}</span>
                <i class="pi pi-user-plus text-600 mr-2 text-primary"></i>
            </span>
        </div>

        <div v-if="loaded && recipientsAllowed && recipientsAllowed.length && !recipientChoosen" class="w-full mb-3">
            <span class="cursor-pointer text-primary" @click="addRecipient(false)">
                <span class="mr-2">{{ lang('select_recipient') }}</span>
                <i class="pi pi-users text-600 mr-2 text-primary"></i>
            </span>
        </div>

        <div v-if="!recipientChoosen">
            <!--SEND PAY SYS-->
            <span class="text-600">{{ lang('bank_list') }}:</span>
            <PaymentSysDropdown v-if="loaded && receivePaySysList && receivePaySys" 
                :pay_sys="receivePaySys" 
                :currency="currencyGet" 
                type="send" 
                :list="receivePaySysGrouped"
                :headerText="lang('choose_recipients_bank')"
                @update:pay-sys="val => chooseReceivePaySys(val)" />
            <Skeleton v-else height="3rem" class="mt-2 mb-3 border-round-xl"/>

            <!--RECIPIENT FIELDS-->
            <RecipientFields v-if="receivePaySys && (receivePaySys.pay_sys || receivePaySys.bank)" 
                @check_valid="setValidReceipent" 
                @return_fields="setFields"
                :fields="receivePaySys?.bank?.['recipient_fields'] && receivePaySys?.bank?.['recipient_fields'].length ? receivePaySys?.bank?.['recipient_fields'] : receivePaySys?.pay_sys?.['recipient_fields']" 
                :requiredFields="receivePaySys?.bank?.['recipient_fields_required'] && receivePaySys?.bank?.['recipient_fields_required'].length ? receivePaySys?.bank?.['recipient_fields_required'] : receivePaySys?.pay_sys?.['recipient_fields_required']" 
                :currency="currencyReceive"/>

        </div>

        <!--DESCRIPTION-->
        <div v-if="loaded">
            <div v-if="!hide_desc">
                <span class="text-600">{{ lang('description') }}:</span>
                <Textarea rows="5" cols="20" class="w-full surface-border border-2 border-round-xl text-sm p-2 mt-2 mb-3" 
                    :value="description" :placeholder="lang('description_text')"
                    @input="description = $event.target.value"
                    style="resize: none !important; font-family: inherit;"
                    />
            </div>
            
            <div v-if="hide_desc" class="w-full mb-3">
                <span class="cursor-pointer text-primary" @click="showDesc(true)">
                    <span class="mr-2">{{ lang('show_description') }}</span>
                    <i class="pi pi-pen-to-square text-600 mr-2 text-primary"></i>
                </span>
            </div>

            <div v-if="!hide_desc" class="w-full mb-3">
                <span class="cursor-pointer text-primary" @click="showDesc(false)">
                    <span class="mr-2">{{ lang('hide_description') }}</span>
                    <i class="pi pi-replay text-600 mr-2 text-primary"></i>
                </span>
            </div>
        </div>
        <Skeleton v-else height="5rem" class="border-round-xl mt-2 mb-3"/>

        <!--BUTTON-->
        <div class="w-full" v-if="!isAuth">
            <Button @click="goToRegistration()" :label="lang('button')+'*'" class="p-button-lg w-full border-round-2xl" :disabled="!checkTotalValid || !loaded" :loading="isLoading" />
        </div>

        <div class="flex w-full gap-1" v-if="isAuth">
            <Button @click='refresh()' :loading="!loaded || isLoading" class="p-button-lg border-round-2xl flex surface-100 text-600 border-0" icon="pi pi-refresh" style="min-width:70px"/>
            <Button @click="Create()" :label="lang('button')" :disabled="!checkTotalValid || !loaded || isLoading || !amountGet" :loading="isLoading"
                class="p-button-lg border-round-2xl flex flex-1"  />
        </div>
        
        <span v-if="!isAuth" class="field flex text-sm text-500 mt-2 align-items-center justify-content-center w-full text-center">
            {{ lang('before') }} *
        </span>

    </div>

    <!--ERRORS-->
    <div v-if="totalError" class="w-full max-w-30rem">
        <InlineMessage severity="warn" class="border-round-2xl text-sm">
            {{ 
                (totalErrorMessage == 'Error getting all merchs' || totalErrorMessage == 'Error getting all merchants') ? lang('critical_error') : 
                (totalErrorMessage == 'No rates loaded') ? lang('no_rates_loaded') :
                (totalErrorMessage == 'No rates filtered') ? lang('no_rates_filtered') : 
                (totalErrorMessage == 'No send pay sys' || totalErrorMessage == 'No get pay sys') ? lang('no_send_pay_sys') : totalErrorMessage
            }}
        </InlineMessage>
    </div>
    <Dialog v-model:visible="firstTransferDisplay" modal :header="lang('first_transfer_title')" 
        class="max-w-30rem ml-4 mr-4 border-round-xl border-none">
        <p>{{ lang('first_transfer_text') }}</p>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/Connect'
import Event from '@/assets/js/Event'
import BigNumber from 'bignumber.js'
import Helper from '@/assets/js/Helper'
import { mapActions } from "vuex"
import { reactive, ref } from 'vue'
import RecipientFields from '@/components/widgets/RecipientFields.vue'
import InputAmount from '@/components/widgets/transfer/InputAmount.vue'
import PaymentSysDropdown from '@/components/widgets/transfer/PaymentSysDropdown.vue'
import RecipientsDropdown from '@/components/widgets/transfer/RecipientsDropdown.vue'
import FbConversion from '@/assets/js/classes/FbConversion.js'
import MobMenu from '@/components/sides/menu/MobMenu.vue'
import TransferCalculation from '@/assets/js/classes/TransferCalculation.js'
import { PaymentSystemList, Bank, BankList } from '@/assets/js/classes/PaymentSystem.js'
import { RateList } from '@/assets/js/classes/Rate.js'
import { RecipientList } from '@/assets/js/classes/Recipient.js'
import { CurrencyList} from '@/assets/js/classes/Currency.js'

export default {
	name: 'TransferFormCreate',
    props: ['currency_receive_first', 'filterPaySys'],
	data() {
		return {
            loaded: false,
            isLoading: false,
            isRecalculating: false,
            totalError: false,
            totalErrorMessage: '',

            // классы
            arrRates: [],
            arrBanks: [],
            arrCurrencies: {},
            arrPaymetSystems: [],

            // валюты и суммы
			amountGet: null,
            amountReceive: null,
            currencyGet: null,
            currenciesGet: [],
            currencyReceive: null,
            currenciesReceive: [],

            amountReceiveList: [],
            amountReceiveSelected: null,

            minGetLimit: null,
			maxGetLimit: null,
            minReceiveLimit: null,
            maxReceiveLimit: null,
            firstTransferSendLimit: null,
            firstTransferReceiveLimit: null,

            description: null,
            hide_desc: true,

            // получатели
            recipients: [],
            recipientsAllowed: [],
            recipientChoosen: null,
            hideRecipients: false,
            recipientFieldsValid: false,
            recipientFields: {},
            
            // инфо
			comission_service: null, 
			comissionToDisplay: null,
            comission_bank: null,
            comission_bank_display: null,
            comission_paysystems: null,
            comission_paysystems_display: null,
            resultRate: null,
            time: null,
            timeOnDisplay: null,
            infoOnDisplay: false,
			rateOnDisplay: false,
            firstTransferDisplay: false,
            isFirstTransfer: false,
            canFirstTransfer: false,
            firstTransferIsOn: false,
            useCashback: true,
            cashbackToDisplay: null,

            // платежные системы
			getPaySys: null, 
            getPaySysList: [],
            sendPaySysListGrouped: [],

            receivePaySys: null,
            receivePaySysList: [],
            receivePaySysGrouped: [],

            CALC: null,
		}
	},

    // задаем переменные для отслехивания изменений
    setup () {
        const react = reactive({
            recalculating_: false,
        })
        return { react }
    },
    components: {
        RecipientFields,
        InputAmount,
        PaymentSysDropdown,
        RecipientsDropdown,
        MobMenu
    },
	watch: {
        totalError: function (val) {
            if(!val || !this.totalErrorMessage) return
            this.$emit('errorMessage', this.totalErrorMessage)
            if(this.totalErrorMessage=='Error getting all merchants'){
                this.$emit('error', true)
            }
        },
        filterPaySys: function (that) {
            this.isRecalculating = true
            this.react.recalculating_ = true
            this.Recalculate('filterPaySys', this.filterPaySys).then(() => {
                this.isRecalculating = false
                this.react.recalculating_ = false
            })
        },
        firstTransferIsOn: function (that) {
            this.isRecalculating = true
            this.react.recalculating_ = true
            if(this.firstTransferIsOn){
                this.Recalculate('first_transfer', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            } else {
                this.Recalculate('first_transfer', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
        },
        useCashback: function () {
            if(this.loaded && !this.isRecalculating){
                this.isRecalculating = true
                this.react.recalculating_ = true
                const from = (this.amountReceiveSelected) ? 'amount_receive_selected' : 'get_amount'
                this.Recalculate(from, this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
        },
        amountGet: function () {
            if(this.loaded && !this.isRecalculating){
                this.isRecalculating = true
                this.react.recalculating_ = true
			    this.Recalculate('get_amount', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
		},
        amountReceive: function () { 
            if(this.loaded && !this.isRecalculating && !this.react.recalculating_){
                this.isRecalculating = true
                this.react.recalculating_ = true
			    this.Recalculate('amount_receive', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
		},
        amountReceiveSelected: function () {
            if(this.loaded && !this.isRecalculating && this.amountReceiveSelected){
                this.isRecalculating = true
                this.react.recalculating_ = true
			    this.Recalculate('amount_receive_selected', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
		},
		currencyGet: function (that) {
            if(this.loaded && !this.isRecalculating){
                this.isRecalculating = true
                this.react.recalculating_ = true
			    this.Recalculate('currency_get', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
		},
        currencyReceive: function (that) {
            if(this.loaded && !this.isRecalculating){
                this.isRecalculating = true
                this.react.recalculating_ = true
			    this.Recalculate('currency_receive', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
		},
        recipientChoosen: function (that) {
            if(!that) return
            if(this.loaded && !this.isRecalculating){
                this.receivePaySys = this.receivePaySysList.find(item => item.bank.code == that.bank_code)
                if(!this.receivePaySys) return
                this.isRecalculating = true
                this.react.recalculating_ = true
                this.Recalculate('receive_pay_sys', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
        }
	},
	methods: {
        lang(value){ return this.$t(`views.transfer.${value}`)},
        chooseGetPaySys(item){
            if(this.loaded && !this.isRecalculating){
                this.getPaySys = item, this.isRecalculating = true, this.react.recalculating_ = true
                this.Recalculate('get_pay_sys', this.filterPaySys).then(res => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })
            }
            try { this.event.send('transferGetPaySysChosen', {custom_data: code}) } catch {}
        },
        chooseReceivePaySys(item){
            if(this.loaded && !this.isRecalculating){
                this.receivePaySys = item, this.isRecalculating = true, this.react.recalculating_ = true
                this.Recalculate('receive_pay_sys', this.filterPaySys).then(res => {
                    this.isRecalculating = false,
                    this.react.recalculating_ = false
                })
            }
            try { this.event.send('transferSendPaySysChosen', {custom_data: code}) } catch {}
        },
        showDesc(val){
            this.hide_desc = !val
            if(!val) this.description = null
        },
        goToRequest(){
            this.$emit('goToRequest')
        },
        setValidReceipent(value){
            this.recipientFieldsValid = (value) ? true : false
        },
        setFields(fields){
            this.recipientFields = fields
        },
        getBigRate(rate){
            if(!rate) return ''
            let big_rate = new BigNumber(rate)
            let one = new BigNumber(1)
            if(big_rate.isNaN()) return ''
            if(big_rate.isLessThan(1)) return one.dividedBy(big_rate).toFixed(2)
            else return big_rate.toFixed(2)
        },
        // форматирование массива сумм для отображения
        format_amount_array(numArr){
            let result = []
            for(let i = 0; i < numArr.length; i++){
                result.push({
                    name: this.splitNum(numArr[i]),
                    code: numArr[i]
                })
            }
            return result
        },
        // расчет курса с учетом комиссий
        calcResultRate(amountGet, amountReceive){
            const b_amount_send = new BigNumber(amountGet)
            const b_amount_receive = new BigNumber(amountReceive)
            if(b_amount_send.isNaN() || b_amount_receive.isNaN()) return ''
            return b_amount_send.dividedBy(b_amount_receive).toFixed(16)
        }, 
        getFormatTime(time){
            if(!time) return ''
            // перевести милисекунды в часы, минуты, и дни
            let seconds = time / 1000
            let minutes = seconds / 60
            let hours = minutes / 60
            let days = hours / 24
            // вывести в формате дни, часы, минуты
            if(days >= 1) return `${Math.floor(days)} ${this.lang('days')}`
            if(hours >= 1) return `${Math.floor(hours)} ${this.lang('hours')}`
            if(minutes >= 1) return `${Math.floor(minutes)} ${this.lang('minutes')}`
            return `${Math.floor(seconds)} ${this.lang('seconds')}`
        },
        getCurSign(cur){
            if(!cur || !cur.sign) return ''
            return cur.sign
        },
        get_code(obj){
            if(!obj || obj.code == undefined) return ''
            return obj.code
        },
        getBankCodeFromPaySys(obj){
            if(!obj || !obj.bank || !obj.bank.code) return null
            return obj.bank.code
        },
        get_bank_title(code){
            return this.helper.get_bank_title(code, this.$store.state.payment.banks, this.lang_)
        },
        get_bank_image(code){
            return this.helper.get_bank_image(code, this.$store.state.payment.banks)
        },
        splitNum(num){
            return this.helper.split_num(num)
        },
        // создание нового получателя
        addRecipient(val){
            if(val){
                this.recipientChoosen = null
                this.hideRecipients = true
            } else {
                this.recipientChoosen = (this.recipientsAllowed && this.recipientsAllowed.length) ? this.recipientsAllowed[0] : null
                this.hideRecipients = false
            }
        },
        show_info(){
            if(this.infoOnDisplay){
                this.infoOnDisplay = false
            } else {
                this.infoOnDisplay = true
            }
        },
        filterRatesBySendPaysCodes(rates, filterCodes){
            if(!filterCodes) return rates
            return rates.filter(rate => {
                if(rate.send_banks) {
                    for(let i = 0; i < rate.send_banks.length; i++){
                        const bank = rate.send_banks[i]
                        if(filterCodes.includes(bank.type)) return true
                    }
                }
            })
        },
        compareMaxAmountArr(val, arr){
            if(!val || !arr || !arr.length) return arr
            const bVal = new BigNumber(val)
            let result = []
            for(let i = 0; i < arr.length; i++){
                const item = new BigNumber(arr[i])
                if(item.isLessThanOrEqualTo(bVal)) result.push(arr[i])
            }
            return (result.length) ? result : arr
        },
        clearAmounts(){
            this.amountReceive = '0'
            this.amountGet = '0'
        },
        ...mapActions(["ActionTransfer", "ActionGetAllWallets"]),
        // Получение массива валют отправки
        getSendCurrencies(filteredRates){
            if(!filteredRates) return []
            if(!Array.isArray(filteredRates) || !filteredRates.length) return []
            if(!this.arrCurrencies || Object.keys(this.arrCurrencies).length === 0) return []
            let currencies_obj = {}
            for (const [k, v] of Object.entries(this.arrCurrencies)) {
                for(let i = 0; i < filteredRates.length; i++) {
                    if(filteredRates[i].from_currency_code == v.code) currencies_obj[v.code] = v
                }
            }
            const values = Object.values(currencies_obj)
            values.sort((a, b) => {
                const valA = a.sort !== undefined ? a.sort : 9999
                const valB = b.sort !== undefined ? b.sort : 9999
                return valA - valB
            })
            return values
        },
		async Recalculate(from=null, filterCodes=null) {

            this.totalError = false
            this.totalErrorMessage = ''

            // console.log('from: ', from)
            // if(filterCodes) console.log('filterCodes: ', filterCodes)

            if(!this.arrRates) {
                this.totalError = true
                this.totalErrorMessage = 'No rates'
                return
            }

            // Фильтруем курсы по платежным системам отправки
            const filteredRates = this.filterRatesBySendPaysCodes(this.arrRates, filterCodes)
            if(!filteredRates || !filteredRates.length) {
                this.totalError = true
                this.totalErrorMessage = 'No rates filtered'
                return
            }

            // Определеяем валюты отправки
            if(from=='start' || from=='filterPaySys'){
                this.currenciesGet = this.getSendCurrencies(filteredRates)
                this.currencyGet = this.currenciesGet[0]
            }
            if(!this.currencyGet) {
                this.totalError = true
                this.totalErrorMessage = 'No currency send'
                return
            }

            // Определяем валюты получения (при смене валюты отправки)
            if(from=='start' || from=='currency_get' || from=='filterPaySys'){
                this.currenciesReceive = CurrencyList.GetFilteredCurrencies(filteredRates, this.currencyGet, 'get', this.$store)
                this.currencyReceive = this.currenciesReceive.length > 0
                    ? (this.currency_receive_first && this.currenciesReceive.find(item => item.code == this.currency_receive_first)
                        ? this.currenciesReceive.find(item => item.code == this.currency_receive_first)
                        : this.currenciesReceive[0])
                    : null
            }
            if(!this.currencyReceive) {
                this.totalError = true
                this.totalErrorMessage = 'No currency receive'
                return
            }

            // Определяем объект расчета
            const CALC = new TransferCalculation({})
            CALC.from_currency_code = this.currencyGet.code
            CALC.to_currency_code = this.currencyReceive.code
            
            // Ищем текущий курс
            const rate = this.getRateFromArray(filteredRates, this.currencyGet.code, this.currencyReceive.code)
            if(!rate) {
                this.totalError = true
                this.totalErrorMessage = 'No rate found' // TODO: добавить сообщение об ошибке
                return
            }

            // Задаем курс
            CALC.setRate(rate)
            // console.log('RATE FOR CALC: ', rate)

            // Определяем платежные системы отправителя (банки)
            if(from=='start' || from=='currency_get' || from=='currency_receive' || from=='filterPaySys'){
                const getListRaw = await PaymentSystemList.RecognizePays(rate, 'get', this.$store)
                this.getPaySysList = TransferCalculation.normalizeInternalPaySystems(getListRaw, {
                    depositWalletId: this.depositWalletId,
                    depositBalance: 0                       // временно отключаем вывод с внутреннего счета
                    // depositBalance: this.depositBalance 
                })
                this.getPaySys = (this.getPaySysList[0]) ? this.getPaySysList[0] : null
                this.sendPaySysListGrouped = PaymentSystemList.GroupByType(this.getPaySysList)
            }
            if(!this.getPaySys) {
                this.totalError = true
                this.totalErrorMessage = 'No get pay sys' // TODO: добавить сообщение об ошибке
                return
            }

            // Определяем платежные системы получателя (банки)
            if(from=='start' || from=='currency_get' || from=='currency_receive' || from=='filterPaySys'){
                const receivePaySysRaw = await PaymentSystemList.RecognizePays(rate, 'send', this.$store, filterCodes)
                this.receivePaySysList = TransferCalculation.normalizeInternalPaySystems(receivePaySysRaw, {
                    depositWalletId: this.depositWalletId,
                    depositBalance: this.depositBalance
                })
                this.receivePaySysGrouped = PaymentSystemList.GroupByType(this.receivePaySysList)
            }

            // Определяем получателей
            // Определяем допустимые коды банков отправителя
            const allowed_send_bank_codes = this.receivePaySysList
                .map(item => (item.bank && item.bank.code) ? item.bank.code : null)
                .filter(Boolean)
            const allowedSendPayCodes = this.receivePaySysList
                .map(item => (item.pay_sys && item.pay_sys.code) ? item.pay_sys.code : null)
                .filter(Boolean)

            // Фильтруем получателей по валюте и банку
            this.recipientsAllowed = RecipientList.FilterRecipients(
                this.recipients,
                this.currencyReceive.code,
                allowed_send_bank_codes,
                allowedSendPayCodes
            )
            // console.log('RECIPIENTS ALLOWED: ', this.recipientsAllowed)

            if(from=='start' || from=='currency_get' || from=='currency_receive' || from=='filterPaySys'){
                // На основе получаетелей и имеющихся платежных систем и банков - определить первую подходящую платежную систему
                const recipientPaySysCodes = this.recipientsAllowed.map(item => item.payment_system_code)
                if(recipientPaySysCodes && recipientPaySysCodes.length){
                    this.receivePaySys = this.receivePaySysList.find(item => recipientPaySysCodes.includes(item.pay_sys.code))
                } else this.receivePaySys = (this.receivePaySysList[0]) ? this.receivePaySysList[0] : null
            }

            // Проверка наличия платежной системы получателя
            if(!this.receivePaySys) {
                this.totalError = true
                this.totalErrorMessage = 'No send pay sys' // TODO: добавить сообщение об ошибке
                return
            }

            // Задаем платежные системы            
            CALC.setGetPaySys(this.getPaySys.pay_sys)
            CALC.setSendPaySys(this.receivePaySys.pay_sys)

            // Банки
            CALC.setGetBank(this.getPaySys.bank)
            CALC.setSendBank(this.receivePaySys.bank)
       
            // Корректировка мерчантов в зависимости от типа платежной системы
            if(this.getPaySys.pay_sys.type!='merchant') CALC.merchants.get.percent = 0
            if(this.receivePaySys.pay_sys.type!='merchant') CALC.merchants.send.percent = 0

            // Определяем флаг бесплатного перевода
            const effectiveFree = this.firstTransferIsOn || (this.canFirstTransfer && this.freeTransfersIsAvailable)
            if(!this.cashbackAvailable) this.useCashback = false
            CALC.setPartnerCashback((this.useCashback && this.cashbackAvailable) ? this.partnerBalance : 0)

            // Вычисляем лимиты на отправку
            this.minGetLimit = CALC.getMinGetLimit()
            this.maxGetLimit = CALC.getMaxGetLimit()

            this.firstTransferSendLimit = CALC.getFreeTransferSendLimit()
            // Отменяем возможность первого перевода, если лимит меньше либо равен нулю
            // UPD: не работает, если не задана сумма первого перевода
            // if(this.firstTransferSendLimit && this.firstTransferSendLimit.isLessThanOrEqualTo(0)) this.canFirstTransfer = false
            this.firstTransferReceiveLimit = CALC.calcRecipientAmountBySenderAmount(this.firstTransferSendLimit, true)

            // сбрасываем выбранную сумму к получению при смене других параметров
            if(from!='amount_receive_selected'){
                this.amountReceiveSelected = null
            }

            const bMinGetLimit = new BigNumber(this.minGetLimit)
            const bMaxGetLimit = new BigNumber(this.maxGetLimit)

            if(!this.amountGet) this.amountGet = this.minGetLimit

            // Проверка суммы ПРИЕМА по лимитам
            const bAmountGet = new BigNumber(this.amountGet)
            if(bAmountGet.isLessThan(bMinGetLimit)) this.amountGet = this.minGetLimit
            if(bAmountGet.isGreaterThan(bMaxGetLimit)) this.amountGet = this.maxGetLimit

            // Задаем сумму ПРИЕМА
            CALC.sender.amount = new BigNumber(this.amountGet)
            CALC.recipient.amount_usdt = CALC.calcRecipientAmountUsdt(effectiveFree)
            CALC.recipient.amount = CALC.calcRecipientAmount(effectiveFree) 
            if(from!='amount_receive' && from!='amount_receive_selected') this.amountReceive = CALC.recipient.amount.toFixed(16)
            
            // коректируем amount по лимитам при переключении валюты отправки
            if(this.amountGet == null){
                this.amountGet = this.minGetLimit
                CALC.sender.amount = new BigNumber(this.amountGet)
            }

            // Вычисляем лимиты на получение 
            this.minReceiveLimit = CALC.calcRecipientAmountBySenderAmount(new BigNumber(this.minGetLimit), effectiveFree).toFixed(2)
            this.maxReceiveLimit = CALC.calcRecipientAmountBySenderAmount(new BigNumber(this.maxGetLimit), effectiveFree).toFixed(2)

            // в зависимости от банка получения определяем – реквизиты получения, комисии, лимиты
            if(from=='start' || from=='currency_get' || from=='currency_receive' || from=='get_pay_sys' || from=='receive_pay_sys' || from=='filterPaySys'){
                // Для массива сумм - формируем массив сумм к получению
                const amountArray = Bank.GetAmountList(this.receivePaySys.bank)
                if(amountArray && amountArray.length > 0) { 
                    const filteredArray = CALC.filterAmountListByLimits(amountArray, this.minReceiveLimit, this.maxReceiveLimit)
                    this.minReceiveLimit = (filteredArray && filteredArray.length > 0) ? filteredArray[0] : this.minReceiveLimit
                    this.maxReceiveLimit = (filteredArray && filteredArray.length > 0) ? filteredArray[filteredArray.length -1] : this.maxReceiveLimit
                    this.amountReceiveList = this.format_amount_array(filteredArray)
                    this.amountReceiveSelected = (this.amountReceiveList) ? this.amountReceiveList[0] : null
                    const atmAmount = (this.amountReceiveList) ? this.amountReceiveList[0].code : null

                    // считаем сумму к отправке на основе выбранной суммы к получению
                    CALC.recipient.amount = new BigNumber(atmAmount)
                    CALC.sender.amount = CALC.calcSenderAmount(effectiveFree)
                    this.amountGet = CALC.sender.amount.toFixed(16)
                } else {
                    this.amountReceiveList = []
                    this.amountReceiveSelected = null
                }
            }

            const maxSendCalcLimit = CALC.getMaxSendLimit(this.receivePaySys)

            // если первый перевод - обнуляем комиссии, задаем сумму отправки и получения
            if(from=='first_transfer'){
                if(this.firstTransferIsOn){
                    this.amountGet = this.firstTransferSendLimit.toString()
                    this.amountReceive = this.firstTransferReceiveLimit.toString()
                    CALC.recipient.amount = new BigNumber(this.amountReceive)
                    CALC.sender.amount = new BigNumber(this.amountGet)
                } else {
                    this.amountGet = this.minGetLimit
                }
            } else this.firstTransferIsOn = false

            // Расчитываем сумму получения или отправки
            if(from=='amount_receive' || from=='amount_receive_selected' ){
                // Корректируем сумму получения по лимитам
                const maxReceiveLimit = new BigNumber(this.maxReceiveLimit)
                const bAmountReceive = new BigNumber(this.amountReceive)
                if(bAmountReceive.isGreaterThan(maxReceiveLimit)) this.amountReceive = this.maxReceiveLimit

                if(from=='amount_receive_selected') CALC.recipient.amount = new BigNumber(this.amountReceiveSelected.code)
                else CALC.recipient.amount = new BigNumber(this.amountReceive)

                this.amountGet = CALC.calcSenderAmount(effectiveFree).toFixed(16)
                CALC.sender.amount = new BigNumber(this.amountGet)
            }

            this.amountReceive = CALC.recipient.amount.toFixed(16)

            // выводим комиссии на дисплей
            this.comissionToDisplay = this.splitNum(CALC.calcAllComissions(effectiveFree).toString()).toString() + ' USDT'
            this.cashbackToDisplay = this.splitNum(CALC.partner.amount.toString()).toString() + ' USDT'

            // Выбираем получателя
            if(from=='start' || from=='currency_get' || from=='currency_receive' || from=='receive_pay_sys' || from=='filterPaySys'){
            // if(!this.recipientChoosen){
                // Сохраняем выбранный код банка отправителя
                const choosenPaySysCode = (this.receivePaySys.pay_sys && this.receivePaySys.pay_sys.code) ? this.receivePaySys.pay_sys.code : null
                const choosenSendBankCode = (this.receivePaySys.bank && this.receivePaySys.bank.code) ? this.receivePaySys.bank.code : null
                const currentRecipientBankCode = (this.recipientChoosen && this.recipientChoosen.bank_code) ? this.recipientChoosen.bank_code : null
                const currentRecipientPaySysCode = (this.recipientChoosen && this.recipientChoosen.payment_system_code) ? this.recipientChoosen.payment_system_code : null

                // Если есть из кого выбирать
                if(this.recipientsAllowed.length > 0){
                    // console.log('CASE 1')
                    if(currentRecipientBankCode == choosenSendBankCode){
                        // console.log('CASE 1-1')
                    } else {

                        // console.log('CASE 1-2')
                        const found = this.recipientsAllowed.find(item => item.bank_code === choosenSendBankCode && item.payment_system_code === choosenPaySysCode)
                        // Если выбранный получатель найден. Если нет, то выбираем первого
                        if(found) {
                            // console.log('CASE 1-2-1')
                            this.recipientChoosen = found
                            this.hideRecipients = false
                        }
                        else {
                            // console.log('CASE 1-2-2')
                            if(from=='receive_pay_sys'){
                                // console.log('CASE 1-2-2-1')
                                this.recipientChoosen = null
                                this.hideRecipients = true
                            } else {
                                // console.log('CASE 1-2-2-2')
                                this.recipientChoosen = this.recipientsAllowed[0]
                                this.hideRecipients = false
                            }
                        }
                    }
                } else {
                    // console.log('CASE 2')
                    this.recipientsAllowed = []
                    this.hideRecipients = true
                    this.recipientChoosen = null
                }
            }
            
            CALC.calcResultRate()
            this.resultRate = CALC.rate.value.toFixed(16)
            this.CALC = CALC
            // console.log('RECALCULATE DONE', CALC.toObject())
            // console.log('GET PAYs' , this.getPaySys)
            // console.log('SEND PAYs' , this.receivePaySys)

            return
		},
        refresh: async function (that) {
            this.loaded = false
            this.isRecalculating = true
            this.react.recalculating_ = true

            try {
                const rates = await this.connect.GetRatesByType('transfer').then(res => res)
                if (!rates.result || !rates.data || rates.data.length <= 0){
                    this.totalError = true
                    if(rates.errors && rates.errors.length > 0) this.totalErrorMessage = rates.errors[0]
                    else this.totalErrorMessage = 'No rates loaded' // TODO: вывести ошибку на разных языках
                    return
                } else this.arrRates = rates.data

                // Обновление курсов перед перерасчетом
                this.Recalculate('start', this.filterPaySys).then(() => {
                    this.isRecalculating = false
                    this.react.recalculating_ = false
                })

                // Обновление балансов пользователей после обновления курсов
                await this.ActionGetAllWallets().then(res=>res)
            } catch (e) {
                console.log('error refreshing rates', e)
            }

            this.loaded = true
            this.isRecalculating = false
            this.react.recalculating_ = false
        },
        getRateFromArray(rates_array, from_currency_code, to_currency_code){
            if(!rates_array || !rates_array.length) return null
            for(let i = 0; i < rates_array.length; i++){
                if(rates_array[i].from_currency_code == from_currency_code && rates_array[i].to_currency_code == to_currency_code) 
                    return rates_array[i]
            }
            return null
        },
		showRateDisplay() {
			if(this.infoOnDisplay) this.rateOnDisplay = true
		},
        showFirstTransferDisplay() {
			this.firstTransferDisplay = true
		},
		goToRegistration() {
            try { this.ActionTransfer(this.recipientFields).then(res => res) } 
            catch (e) { console.log('error set store', e)}
            this.event.send('transferCreateReg')
			this.$router.push({ path: '/reg/'})
		},
		showMess(severity, summary, detail) {
            try{ this.$toast.add({ severity: severity, summary: summary, detail: detail, life: 3000 }) }
            catch(e) { console.log('error showMess', e) }
        },
        goToProfile(){
            this.$router.push({ path: '/personal/profile/'})
        },
        getDepositWalletId(list) {
            if(!list || !Array.isArray(list)) return null
            if(list && list.length > 0) {
                for (let i = 0; i < list.length; i++) {
                    if(list[i].type == 'deposit') return list[i].id
                }
            } else return null
        },
        getPartnerWallet(list) {
            if(!list || !Array.isArray(list)) return null
            return list.find(item => item && (item.ext_type == 'partner' || item.type == 'partner')) || null
        },
        getBalanceValue(value) {
            if(value && value.$numberDecimal !== undefined) return value.$numberDecimal
            return value || 0
        },
        // TODO: перенести в store
        async LoadStatOfUser(){
            this.canFirstTransfer = false
            const stats = await this.connect.user_get_stat().then(res => res)
            if (stats.result && stats.data && stats.data.stats) {
                try{
                    if(stats.data.stats.transfers.completed == 0) this.canFirstTransfer = true
                } catch {}
            }
        },
        async LoadRecipients(){
            const recipients = await RecipientList.GetList(this.$store).then(res => res)
            if (recipients && recipients.length > 0) {
                this.recipients = recipients
            }
        },
        async Create(){
            this.isLoading = true 

            if (!this.checkTotalValid) {
                this.isLoading = false
                this.showMess('error', this.lang('error_title'), '')
                return
            }

            const requiresVerification = [this.getPaySys, this.receivePaySys].some(item => {
                return item && item.pay_sys && item.pay_sys.kyc_required === true
            })
            if(requiresVerification && this.isUserVerified !== true){
                this.isLoading = false
                this.showMess('warn', this.lang('verification_required_title'), this.lang('verification_required_text'))
                return
            }

            // если выбран получатель, то берем его id
            const recipient_id = (this.recipientChoosen && this.recipientChoosen.id) ? this.recipientChoosen.id : null
            const recipientBankCode = (this.recipientChoosen && this.recipientChoosen.bank_code) ? this.recipientChoosen.bank_code : null
            
            let fbp, fbc, url
            try{
                fbp = this.getFbc
                fbc = this.getFbc
                url = this.getUrl
            } catch(e) {
                console.log('error', e)
            }
            // console.log('CALC', this.CALC.toObject())
            const cashbackAmount = (this.CALC && this.CALC.partner && this.CALC.partner.amount) ? this.CALC.partner.amount : new BigNumber(0)
            const cashback = (this.partnerWalletId && cashbackAmount.isGreaterThan(0)) ? {
                wallet_id: this.partnerWalletId,
                amount: cashbackAmount.toString(),
                currency_code: 'usdt'
            } : null
        
            const data = {
                rate: this.CALC.rate.value.toString(),
                free: this.firstTransferIsOn || (this.canFirstTransfer && this.freeTransfersIsAvailable),
                cashback: cashback,
                source: (this.isTgApp) ? 'tgapp' : 'web', 

                sender: {
                    currency_code: this.get_code(this.currencyGet),
                    amount: this.CALC.sender.amount.toString(),
                    payment_system_code: this.getPaySys.pay_sys.code,
                    payment_system_type: this.getPaySys.pay_sys.type,
                    bank_code: this.getBankCodeFromPaySys(this.getPaySys),
                    description: this.description,
                },

                recipient: {
                    recipient_id: recipient_id,
                    wallet_id: this.depositWalletId,
                    currency_code: this.get_code(this.currencyReceive),
                    amount: this.CALC.recipient.amount.toString(),
                    payment_system_code: (this.receivePaySys && this.receivePaySys.pay_sys && this.receivePaySys.pay_sys.code) ? this.receivePaySys.pay_sys.code : null,
                    payment_system_type: (this.receivePaySys && this.receivePaySys.pay_sys && this.receivePaySys.pay_sys.type) ? this.receivePaySys.pay_sys.type : null,
                    bank_code: (recipientBankCode) ? recipientBankCode : (this.receivePaySys) ? this.getBankCodeFromPaySys(this.receivePaySys) : null,
                    fields: this.recipientFields,
                },
                
                browser: {
                    fbp: fbp,
                    fbc: fbc,
                    url: url
                }
            }
            // console.log('create data', data)

            let transfer = {}

            try{
                transfer = await this.connect.transfer_create(data).then(res=>res)
                if(transfer.result && transfer.data) {
                    this.showMess('success', this.lang('success_title'), this.lang('success_subtitle'))

                    // отправляем событие об успешном создании перевода
                    try {
                        const trId = (transfer.data && transfer.data._id) ? transfer.data._id : null
                        const trSenderId = (transfer.data && transfer.data.sender && transfer.data.sender.user_id) ? transfer.data.sender.user_id : null
                        const eventId = (trId) ? 'transfer-' + trId : null
                        const date = (transfer.data && transfer.data.dates && transfer.data.dates.created) ? transfer.data.dates.created : null
                        const timestamp = Math.floor(date / 1000)
                        const userAgent = this.helper.getUserAgent()
                        
                        let fbConv = new FbConversion('transferCreate')
                        if(eventId)     fbConv.setId(eventId)
                        if(trSenderId)  fbConv.setExternalId(trSenderId)
                        if(timestamp)   fbConv.setTime(timestamp)
                        if(url)         fbConv.setUrl(url)
                        if(fbp)         fbConv.setFbp(fbp)
                        if(fbc)         fbConv.setFbc(fbc)
                        if(userAgent)   fbConv.setUserAgent(userAgent)
                        const eventData = fbConv.get()
                        
                        this.event.send('transferCreate', eventData) 
                    } catch (e) {
                        console.log('error send event', e)
                    }

                    this.$router.push({path: '/personal/transfer/'+transfer.data._id})
                } else {
                    if(transfer.error && this.getErrorMessage(transfer.error)) {
                        this.showMess('warn', this.lang('error_title'), this.getErrorMessage(transfer.error)) 
                    } else {
                        if(transfer.message) this.showMess('error', this.lang('error_title'), transfer.message)
                        else this.showMess('error', this.lang('error_title'), 'Unknown error')
                    }
                }
            } catch(e) {
                console.log('error', e)
            }
            this.isLoading = false
        },
        getErrorMessage(code){
            let message = null
            switch(code){
                case 'test_return':
                    message = this.lang('err_test_return')
                    break
                case 'verification_required':
                    message = this.lang('err_verification_required')
                    break
                case 'existing_open_transfers':
                    message = this.lang('err_existing_open_transfers')
                    break
                case 'amounts_not_equal':
                case 'comissions_not_equal':
                    message = this.lang('err_amounts_not_equal')
                    this.refresh()      // обновляем курсы
                    break
                case 'rates_not_equal':
                    message = this.lang('err_rates_not_equal')
                    this.refresh()      // обновляем курсы
                    break
                case "free_transfer_not_available":
                    message = this.lang('err_free_transfer_not_available')
                    this.LoadStatOfUser().then(() => {
                        this.refresh()      // обновляем курсы
                    })
                    break
                default:
                    message = null
            }
            return message
        }
	},
    computed : {
        isAuth: function () { return this.$store.getters.isAuthenticated },
        lang_() { return (this.$i18n.locale) ? this.$i18n.locale : 'ru' },
        user_name: function () { return this.$store.getters.StateUserName },
        screenSize() { return this.$store.getters.StateScreenSize },
        isUserVerified: function () { return this.$store.getters.StateVerified },
        isTgApp: function () { return this.$store.getters.StateTgApp },
        getFbp: function () { return this.helper.getFbp() },
        getFbc: function () { return this.helper.getFbc() },
        getUrl: function () { return this.helper.getUrl() },
        checkTotalValid(){
            if( this.receivePaySys &&  this.receivePaySys.pay_sys && this.receivePaySys.pay_sys.type &&  this.receivePaySys.pay_sys.type=='internal') return true
            if(this.recipientChoosen) return true
            if(!this.recipientFieldsValid) return false
            return true
        },
        depositWalletId() { return this.getDepositWalletId(this.$store.getters.StateWallets)},
        depositBalance() {
            const wallets = this.$store.getters.StateWallets
            if(!wallets || !Array.isArray(wallets)) return null
            for (let i = 0; i < wallets.length; i++) {
                if(wallets[i].type == 'deposit') return wallets[i].balance
            }
            return null
        },
        partnerWallet() {
            return this.getPartnerWallet(this.$store.getters.StateWallets)
        },
        partnerWalletId() {
            return (this.partnerWallet && this.partnerWallet.id) ? this.partnerWallet.id : null
        },
        partnerBalance() {
            const walletBalance = (this.partnerWallet) ? this.getBalanceValue(this.partnerWallet.balance) : this.$store.getters.StatePartnerBalance
            const balance = new BigNumber(walletBalance || 0)
            if(balance.isNaN() || balance.isLessThanOrEqualTo(0)) return 0
            return balance.toString()
        },
        cashbackAvailable() {
            const balance = new BigNumber(this.partnerBalance || 0)
            return !!this.partnerWalletId && !balance.isNaN() && balance.isGreaterThanOrEqualTo(0.1)
        },
        freeTransfersIsAvailable() {
            return this.$store.getters.IsFreeTransfer || false
        }
    },
	async beforeCreate() {
        this.helper = new Helper()
        this.connect = new Connect()
        this.event = new Event(this)
    },
	async mounted() {
        // платежные системы | обязательно
        this.arrPaymetSystems = await PaymentSystemList.GetList(this.$store).then(r=>r)
        if (!this.arrPaymetSystems || this.arrPaymetSystems.length <= 0){
            this.totalError = true
            this.totalErrorMessage = this.lang('err_payment_systems_loaded')
            return
        }

        // валюты | обязательно
        const resCurrencies = await CurrencyList.GetList(this.$store).then(r=>r)
        if (!resCurrencies || resCurrencies.length <= 0){
            this.totalError = true
            this.totalErrorMessage = this.lang('err_no_currencies_loaded')
            return
        }
        this.arrCurrencies = CurrencyList.ToObjectMap(resCurrencies)

        // банки | обязательно
        this.arrBanks = await BankList.GetList(this.$store).then(r=>r)
        if (!this.arrBanks || this.arrBanks.length <= 0){
            this.totalError = true
            this.totalErrorMessage = this.lang('err_no_banks')
            return
        }

        //курсы доступных валют | обязательно
        const resRates = await this.connect.GetRatesByType('transfer').then(res => res)
        if (!resRates.result || !resRates.data || resRates.data.length <= 0){
            this.totalError = true
            if(resRates.errors && resRates.errors.length > 0) this.totalErrorMessage = resRates.errors[0]
            else this.totalErrorMessage = this.lang('critical_error')
            return
        }
        // проверяем на обязательные поля
        const objRates = new RateList(resRates.data)
        this.arrRates = objRates.toArrayObj()

        if(this.isAuth){
            await this.LoadStatOfUser().then(res => res)
            await this.LoadRecipients().then(res => res)
        } else {
            this.recipients = []
            this.canFirstTransfer = true
        }

        this.loaded = true
        this.react.recalculating_ = true
        this.isRecalculating = true

        this.Recalculate('start', this.filterPaySys).then(res => {
            this.react.recalculating_ = false
            this.isRecalculating = false
        })
	}
}
</script>